import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { AuthContextType, LoginResult, MfaVerificationResult } from '@typeDefs/domain/auth';
import { authService, LoginLockedError } from '@services/api/authService';
import { tokenManager } from '@services/auth/tokenManager';
import { idleWatcher } from '@services/auth/idleWatcher';
import { RefreshFailureReason } from '@generated/api';

export const AuthContext = createContext<AuthContextType | null>(null);

export const SESSION_END_REASON_KEY = 'session_end_reason';

// A page-load refresh failing as "unknown" just means "not signed in", so it shows no banner.
// These reasons mean a session really ended, and /login explains them after the reload.
const MOUNT_REFRESH_ANNOUNCED_REASONS: readonly RefreshFailureReason[] = [
  RefreshFailureReason.ReplayDetected,
  RefreshFailureReason.IdleTimeout,
  RefreshFailureReason.Expired,
  RefreshFailureReason.SessionSuperseded,
];

// Frontend deliberately fires 1 min before the backend's 15 min idle ceiling
// (docs/2026-04-22-idle-auto-logout.md §1) so the user sees the warning modal
// before the server-side rotation 401s. The 60 s warning window is the
// countdown shown in IdleWarningModal.
const IDLE_LIMIT_MS = 14 * 60 * 1000;
const IDLE_WARNING_MS = 60 * 1000;

if (import.meta.env.DEV || import.meta.env.MODE === 'test') {
  const debugWindow = window as Window & {
    __tokenManager?: typeof tokenManager;
    __idleWatcher?: typeof idleWatcher;
    __authConfig?: { IDLE_LIMIT_MS: number; IDLE_WARNING_MS: number };
  };
  debugWindow.__tokenManager = tokenManager;
  debugWindow.__idleWatcher = idleWatcher;
  debugWindow.__authConfig = { IDLE_LIMIT_MS, IDLE_WARNING_MS };
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [idleWarningActive, setIdleWarningActive] = useState<boolean>(false);
  const [mfaState, setMfaState] = useState<{
    challengeId: string;
    email: string;
    method: string;
    remainingAttempts: number | null;
    lockedUntil: string | null;
  } | null>(null);

  // Silent refresh on mount
  useEffect(() => {
    const initAuth = async () => {
      setIsLoading(true);
      // Refresh token now lives in an httpOnly cookie — JS cannot check its presence.
      // We always attempt a silent refresh on mount; the server returns 401 if no
      // valid cookie exists, which tokenManager maps to false.
      const success = await tokenManager.attemptRefresh(true, MOUNT_REFRESH_ANNOUNCED_REASONS);
      setIsAuthenticated(success);
      setIsLoading(false);
    };
    initAuth();
  }, []);

  // Listen for session-expired events from tokenManager. The detail.errorCode
  // (when present) is persisted to sessionStorage so the Login page can render
  // the matching banner copy after the redirect.
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ errorCode?: RefreshFailureReason }>).detail;
      if (detail?.errorCode) {
        sessionStorage.setItem(SESSION_END_REASON_KEY, detail.errorCode);
      }
      tokenManager.clearTokens();
      idleWatcher.stop();
      idleWatcher.clearActivity();
      setIdleWarningActive(false);
      setIsAuthenticated(false);
    };
    window.addEventListener('session-expired', handler);
    return () => window.removeEventListener('session-expired', handler);
  }, []);

  // Arm the idle watcher whenever the user is authenticated
  useEffect(() => {
    if (!isAuthenticated) return;
    idleWatcher.start({ idleLimitMs: IDLE_LIMIT_MS, warningMs: IDLE_WARNING_MS });
    return () => {
      idleWatcher.stop();
    };
  }, [isAuthenticated]);

  // Idle events drive the warning modal and auto-logout
  useEffect(() => {
    const onWarning = () => {
      if (!isAuthenticated) return;
      setIdleWarningActive(true);
    };
    const onTimeout = () => {
      if (!isAuthenticated) return;
      setIdleWarningActive(false);
      // Client-side idle path doesn't go through the refresh endpoint, so no
      // errorCode comes back over the wire — set the reason explicitly so the
      // Login banner can show "signed out due to inactivity".
      sessionStorage.setItem(SESSION_END_REASON_KEY, RefreshFailureReason.IdleTimeout);
      void (async () => {
        try {
          await authService.logout();
        } catch (error) {
          console.error('Idle logout API error:', error);
        } finally {
          tokenManager.clearTokens();
          idleWatcher.stop();
          idleWatcher.clearActivity();
          setIsAuthenticated(false);
        }
      })();
    };
    window.addEventListener('session-idle-warning', onWarning);
    window.addEventListener('session-idle-timeout', onTimeout);
    return () => {
      window.removeEventListener('session-idle-warning', onWarning);
      window.removeEventListener('session-idle-timeout', onTimeout);
    };
  }, [isAuthenticated]);

  // Session storage persistence for MFA state
  useEffect(() => {
    const savedMfaState = sessionStorage.getItem('mfa_pending_state');
    if (savedMfaState) {
      try {
        setMfaState(JSON.parse(savedMfaState));
      } catch (error) {
        console.error('Failed to parse MFA state:', error);
        sessionStorage.removeItem('mfa_pending_state');
      }
    }
  }, []);

  useEffect(() => {
    if (mfaState) {
      sessionStorage.setItem('mfa_pending_state', JSON.stringify(mfaState));
    } else {
      sessionStorage.removeItem('mfa_pending_state');
    }
  }, [mfaState]);

  const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    try {
      const response = await authService.login({ email, password });

      if (response.requiresMfa) {
        setMfaState({
          challengeId: response.mfaChallengeId!,
          email: email,
          method: response.mfaMethod || 'Email',
          remainingAttempts: response.remainingAttempts ?? null,
          lockedUntil: response.lockedUntil ?? null,
        });

        return {
          type: 'mfa_required',
          challengeId: response.mfaChallengeId!,
          email: email,
          method: response.mfaMethod || 'Email',
        };
      } else if (response.success && response.token) {
        // Refresh token is delivered via httpOnly cookie by the server — not in the response body.
        tokenManager.setTokens(response.token);
        setIsAuthenticated(true);
        return { type: 'success' };
      } else {
        return {
          type: 'error',
          message: response.error || 'Login failed',
          lockedUntil: response.lockedUntil ?? null,
        };
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'An unexpected error occurred';
      tokenManager.clearTokens();
      setIsAuthenticated(false);
      return {
        type: 'error',
        message,
        lockedUntil: error instanceof LoginLockedError ? error.lockedUntil : null,
      };
    }
  }, []);

  const verifyMfa = useCallback(
    async (challengeId: string, code: string): Promise<MfaVerificationResult> => {
      try {
        const response = await authService.verifyMfa({ challengeId, code });

        if (response.success && response.token) {
          // Refresh token is delivered via httpOnly cookie by the server — not in the response body.
          tokenManager.setTokens(response.token);
          setMfaState(null);
          setIsAuthenticated(true);
          return { type: 'success' };
        } else {
          if (mfaState) {
            setMfaState({
              ...mfaState,
              remainingAttempts: response.remainingAttempts ?? null,
              lockedUntil: response.lockedUntil ?? null,
            });
          }

          return {
            type: 'error',
            message: response.error || 'Invalid verification code',
            remainingAttempts: response.remainingAttempts ?? null,
            lockedUntil: response.lockedUntil ?? null,
          };
        }
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Verification failed';
        return {
          type: 'error',
          message,
          remainingAttempts: mfaState?.remainingAttempts ?? null,
          lockedUntil: mfaState?.lockedUntil ?? null,
        };
      }
    },
    [mfaState],
  );

  const clearMfaState = useCallback((): void => {
    setMfaState(null);
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout API error:', error);
    } finally {
      tokenManager.clearTokens();
      idleWatcher.stop();
      idleWatcher.clearActivity();
      setIdleWarningActive(false);
      setIsAuthenticated(false);
    }
  }, []);

  const dismissIdleWarning = useCallback((): void => {
    setIdleWarningActive(false);
    idleWatcher.recordActivity();
    void tokenManager.attemptRefresh();
  }, []);

  const value = {
    isAuthenticated,
    isLoading,
    login,
    verifyMfa,
    logout,
    clearMfaState,
    mfaState,
    idleWarningActive,
    dismissIdleWarning,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};
